import {TileMedia, TileMediaTracking} from '../../MediaCard';
import {TileMediaSpecKey} from './keys';

export type TileMediaStatus = {
  isLoading: boolean;
  isRequested: boolean;
  isLoaded: boolean;
  hasError: boolean;
  errorMessage?: string;
  lastRequestedAt: number;
  lastUpdatedAt: number;
};

/**
 * Centralizing the shared bits of global state used in `PanelMediaBrowser` fetch. Because this is a conscious ejection from typical state management practices in a React app, I'm abusing the I_AM_A_CONSTANT variable naming convention to hint that this a global state object residing in-memory for each user session.
 */
const IN_MEMORY_CACHE = {
  // File contents pointed to by a download URL should never change, so we should never fetch the same URL more than once.
  BLOB_PROMISE_BY_URL: new Map<string, Promise<Blob>>(),
  // We only ever need one object URL per blob
  OBJECT_URL_BY_BLOB: new Map<Blob, string>(),
  // the global state mapping keys to tile media
  TILE_MEDIA_BY_KEY: new Map<TileMediaSpecKey, TileMedia>(),
  // Detailed status tracking for each tile media request
  TILE_MEDIA_STATUS_BY_KEY: new Map<TileMediaSpecKey, TileMediaStatus>(),
  // Tracking timing information for performance monitoring
  TILE_MEDIA_TRACKING_BY_KEY: new Map<TileMediaSpecKey, TileMediaTracking>(),
} as const;

export function updateTracking(
  key: TileMediaSpecKey,
  property: keyof TileMediaTracking
) {
  const existingTracking = IN_MEMORY_CACHE.TILE_MEDIA_TRACKING_BY_KEY.get(
    key
  ) || {
    availableFilesReceivedMs: 0,
    directUrlReceivedMs: 0,
    dataCachedMs: 0,
  };

  if (property in existingTracking && existingTracking[property] !== 0) {
    return;
  }

  const newTracking = {
    ...existingTracking,
    [property]: Date.now(),
  };

  IN_MEMORY_CACHE.TILE_MEDIA_TRACKING_BY_KEY.set(key, newTracking);
}

export function updateTileMediaStatus(
  key: TileMediaSpecKey,
  status: Partial<TileMediaStatus>
) {
  const existingStatus = IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(key) || {
    isLoading: false,
    isRequested: false,
    isLoaded: false,
    hasError: false,
    lastRequestedAt: 0,
    lastUpdatedAt: 0,
  };

  const newStatus = {
    ...existingStatus,
    ...status,
    lastUpdatedAt: Date.now(),
  };

  IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.set(key, newStatus);
  return newStatus;
}

export default IN_MEMORY_CACHE;
